import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Trophy } from 'lucide-react';
import { buildContentView } from '../../../lib/reporting/socialContent';
import { CenteredSpinner } from '../../../components/ui/Spinner';
import type { DateRange } from '../../../lib/reportingDateRange';
import { getSocialContent } from './api';
import { AsOfLine } from './AsOfLine';
import { ContentPostCard } from './ContentPostCard';
import { FormatSection } from './FormatSection';
import { CadenceSection } from './CadenceSection';
import { BestTimeHeatmap } from './BestTimeHeatmap';
import { StoriesSection } from './StoriesSection';

// Content tab — top / underperforming posts, performance by format, posting
// cadence, and best time to post. FB + IG engagement analytics; GBP appears in
// posting cadence (volume) only — no post analytics to rank. Live Zernio reads,
// scoped to the selected client + period. Self-fetching (Radix unmounts inactive
// tabs), React-Query cached, with the as-of caveat (these are snapshot figures).
//
// Stories are deferred pending Zernio's story-insights answer — a clean seam is
// left below to slot the section in once the webhook token is confirmed.

const CONTENT_STALE_MS = 6 * 60 * 60_000;

export function SocialContentTab({ accountId, range }: { accountId: string; range: DateRange }) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['social-reporting-content', accountId, range.from, range.to],
    queryFn: () => getSocialContent(accountId, range),
    enabled: accountId !== '',
    staleTime: CONTENT_STALE_MS,
  });

  const view = useMemo(() => (query.data ? buildContentView(query.data) : null), [query.data]);

  const refresh = () =>
    qc.invalidateQueries({ queryKey: ['social-reporting-content', accountId, range.from, range.to] });

  if (query.isLoading) {
    return (
      <div className="py-16">
        <CenteredSpinner />
      </div>
    );
  }
  if (query.isError || !view || !query.data) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-6 py-10 text-center">
        <p className="text-sm text-red-700">Couldn't load the content report.</p>
        <button
          type="button"
          onClick={() => query.refetch()}
          className="mt-3 rounded-md border border-red-200 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <AsOfLine lastSync={view.asOf} syncing={query.isFetching} onRefresh={refresh} />
      </div>

      {view.failures.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <div>
            <p className="font-medium">Some data couldn't be loaded.</p>
            <ul className="mt-0.5 list-inside list-disc text-xs text-amber-700">
              {view.failures.map((f) => (
                <li key={f.source}>
                  {f.source}: {f.message}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {query.data.postsCapped && (
        <p className="text-xs text-slate-400">
          This client published more posts than we rank per period — the lists below are the
          top posts by engagement for the period.
        </p>
      )}

      {!view.hasEngagementData ? (
        <EmptyEngagement gbpConnected={view.gbpConnected} />
      ) : (
        /* Top posts — image-forward cards. Auto-fill grid: cards cap at ~400px and
           the column COUNT grows with the viewport (a wide monitor gets more posts
           per row, not bigger images), collapsing to one full-width column on
           mobile (min(100%,400px) so the cap can't overflow narrow screens).
           Left-aligned, so leftover space is margin — never stretched cards. */
        <Section title="Top posts" icon={<Trophy className="h-4 w-4 text-amber-500" aria-hidden />}>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,400px),400px))] justify-start gap-4">
            {view.top.map((post, i) => (
              <ContentPostCard key={post.postId} post={post} rank={i + 1} />
            ))}
          </div>
        </Section>
      )}

      {view.gbpConnected && (
        <p className="text-xs text-slate-400">
          Google Business posts appear in posting cadence only — Google provides no post
          analytics to rank by engagement.
        </p>
      )}

      {/* Performance by format (feed vs reel, by platform). */}
      {view.formatBuckets.length > 0 && (
        <Section title="Performance by format">
          <FormatSection buckets={view.formatBuckets} />
        </Section>
      )}

      {/* Posting cadence — volume vs engagement. */}
      <Section title="Posting cadence" subtitle="posts per week vs engagement">
        <CadenceSection cadence={view.cadence} platforms={view.cadencePlatforms} />
      </Section>

      {/* Best time to post. */}
      <Section title="Best time to post" subtitle="by average engagement">
        <BestTimeHeatmap slots={query.data.bestTimeSlots} />
      </Section>

      {/* Instagram Stories insights (source live|cached|unavailable; renders nothing
          when the account has no retrievable stories). */}
      <StoriesSection stories={query.data.stories} />
    </div>
  );
}

export function Section({
  title,
  subtitle,
  icon,
  children,
}: {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-center gap-2">
        {icon}
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        {subtitle && <span className="text-xs text-slate-400">· {subtitle}</span>}
      </div>
      {children}
    </section>
  );
}

function EmptyEngagement({ gbpConnected }: { gbpConnected: boolean }) {
  return (
    <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-6 py-12 text-center text-sm text-slate-400">
      {gbpConnected
        ? 'No post analytics for this period. Google Business posts have no engagement analytics — this client’s story is in reviews (Community & Reputation) and posting cadence.'
        : 'No posts with analytics in this period.'}
    </div>
  );
}
